import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateOfferModal = ({ open, onClose, request, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    property_name: '',
    room_description: '',
    offered_price: '',
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      // Validate price is within budget range
      const price = parseFloat(formData.offered_price);
      if (price < request.budget_min || price > request.budget_max) {
        toast.error(`Il prezzo deve essere tra €${request.budget_min} e €${request.budget_max}`);
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${API}/offers/create`,
        {
          request_id: request.id,
          property_name: formData.property_name,
          room_description: formData.room_description || null,
          offered_price: price,
          notes: formData.notes || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Offerta inviata con successo!');
      setFormData({
        property_name: '',
        room_description: '',
        offered_price: '',
        notes: ''
      });
      onSuccess && onSuccess(response.data.offer);
      onClose();
    } catch (error) {
      console.error('Error creating offer:', error);
      const errorMsg = error.response?.data?.detail || 'Errore durante l\'invio dell\'offerta';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-none sm:rounded-2xl shadow-2xl w-full sm:max-w-2xl h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <h2 className="text-lg sm:text-2xl font-bold text-slate-900">Fai un'Offerta</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <div className="p-4 sm:p-6">
          {/* Request Details */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <h3 className="font-semibold text-slate-900 mb-2">Dettagli Richiesta</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-slate-600">Città:</span>
                <span className="ml-2 font-medium text-slate-900">{request.city}</span>
              </div>
              <div>
                <span className="text-slate-600">Ospiti:</span>
                <span className="ml-2 font-medium text-slate-900">{request.guests}</span>
              </div>
              <div>
                <span className="text-slate-600">Check-in:</span>
                <span className="ml-2 font-medium text-slate-900">{request.check_in_date}</span>
              </div>
              <div>
                <span className="text-slate-600">Check-out:</span>
                <span className="ml-2 font-medium text-slate-900">{request.check_out_date}</span>
              </div>
              <div className="col-span-2">
                <span className="text-slate-600">Budget Range:</span>
                <span className="ml-2 font-bold text-emerald-600">
                  €{request.budget_min} - €{request.budget_max}
                </span>
              </div>
            </div>
            {request.notes && (
              <div className="mt-3 pt-3 border-t border-blue-200">
                <span className="text-slate-600 text-sm">Note: </span>
                <span className="text-slate-700 text-sm">{request.notes}</span>
              </div>
            )}
          </div>

          {/* Offer Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Nome Proprietà *
              </label>
              <Input
                required
                value={formData.property_name}
                onChange={(e) => setFormData({ ...formData, property_name: e.target.value })}
                placeholder="es. Hotel Bella Vista"
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Prezzo Offerto (per notte) *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">€</span>
                <Input
                  required
                  type="number"
                  step="0.01"
                  min={request.budget_min}
                  max={request.budget_max}
                  value={formData.offered_price}
                  onChange={(e) => setFormData({ ...formData, offered_price: e.target.value })}
                  placeholder={`Tra ${request.budget_min} e ${request.budget_max}`}
                  className="w-full pl-8"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Il prezzo deve essere compreso tra €{request.budget_min} e €{request.budget_max}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Descrizione Camera/Appartamento
              </label>
              <textarea
                value={formData.room_description}
                onChange={(e) => setFormData({ ...formData, room_description: e.target.value })}
                placeholder="es. Camera doppia con bagno privato, aria condizionata, Wi-Fi gratuito..."
                className="w-full min-h-[100px] px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Note Aggiuntive
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="es. Colazione inclusa, check-in flessibile..."
                className="w-full min-h-[80px] px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
                disabled={loading}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800"
                disabled={loading}
              >
                {loading ? 'Invio...' : 'Invia Offerta'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateOfferModal;
